import { Box, Typography } from "@mui/material"
import {
  selectedDetailingSelector,
  selectedEngineSelector,
} from "components/shopFeatures/detailing/detailingSelectors"
import { Font } from "components/shopFeatures/wizard/constants"
import { useTypedSelector } from "store"
import styled, { css, useTheme } from "styled-components"
import { StyledSvgIcon } from "theme/shared"
import PreSaleTag from "./PreSaleTag"

type PopupProps = {
  popup?: boolean
}

const StyledHeadline = styled(Box)`
  margin: ${(p) => p.theme.spacing(2, 0)};
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: normal;
  flex-shrink: 0;

  ${(p) => p.theme.breakpoints.up("smallLaptop")} {
    margin: unset;
  }

  @media (min-width: 1281px) {
    margin-top: ${(p) => p.theme.spacing(3.5)};
  }
`

const StyledModel = styled(Typography).withConfig({
  shouldForwardProp: () => true,
})<{ variant?: string; component?: string }>`
  font-size: 1.3125rem;
  letter-spacing: 2.1px;
  margin: 0;

  ${(p) => p.theme.breakpoints.up("md")} {
    font-family: ${Font.MazdaType};
    font-size: 32px;
    font-weight: 500;
    line-height: 45px;
    letter-spacing: 0.11em;
  }
`

const StyledDetailing = styled(Typography)`
  font-family: ${Font.MazdaType};
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.08em;
  color: ${(p) => p?.theme?.colors?.disable};

  margin-top: ${(p) => p.theme.spacing(0.5)}px;
  margin-bottom: 8px;

  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: ${(p) => p.theme.spacing(1)}px;
    margin-bottom: 16px;
  }
`

const StyledIfPopup = styled.div<{ popup?: boolean; $isPreSale: boolean }>`
  ${(p) =>
    p.popup &&
    css`
      ${StyledHeadline} {
        ${(p) => p.theme.breakpoints.up("md")} {
          margin-top: ${(p) => p.theme.spacing(1.5)}px;
        }
      }

      ${StyledDetailing} {
        font-size: 1rem;
        margin-top: ${(p) => p.theme.spacing(1.5)}px;

        ${(p) => p.theme.breakpoints.up("md")} {
          margin-top: ${(p) => p.theme.spacing(1.5)}px;
        }
      }
    `}
  margin-bottom: -70px;

  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: ${(p) => (p.$isPreSale ? "-70px" : "-100px")};
  }

  @media (min-width: 1281px) {
    margin-bottom: -100px;
  }
`

const StyledPreSaleWrapper = styled.div`
  margin-bottom: 4px;
  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-bottom: 8px;
  }
`

const StyledStockComment = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  span {
    font-family: ${Font.OENaPro};
    font-size: 14px;
    font-weight: 300;
    line-height: 130%;
  }
`

const ModelAndDetailing = ({ popup }: PopupProps) => {
  const model = useTypedSelector((state) => state.wizard.model)
  const typeId = model?.typeId
  const stockComment = model?.stockComment
  const isPreSale = typeId === 3

  const selectedDetailing = useTypedSelector(selectedDetailingSelector)
  const selectedEngine = useTypedSelector(selectedEngineSelector)

  const theme = useTheme()

  if (!selectedDetailing) return null

  const { modelName, name } = selectedDetailing.details

  return (
    <StyledIfPopup popup={popup} $isPreSale={isPreSale}>
      <StyledHeadline>
        {isPreSale && (
          <StyledPreSaleWrapper>
            <PreSaleTag size="large" />
          </StyledPreSaleWrapper>
        )}
        <StyledModel variant="h1">{modelName.toUpperCase()}</StyledModel>
        <StyledDetailing variant="h2">
          {name} {selectedEngine?.engineIdName}
        </StyledDetailing>
        {isPreSale && stockComment && (
          <StyledStockComment>
            <StyledSvgIcon
              src="/icons/info-square-icon.svg"
              $w={16}
              $h={16}
              style={{ minWidth: "16px" }}
            />
            <Typography component="span" color={theme.colors.mazdaRed}>
              {stockComment}
            </Typography>
          </StyledStockComment>
        )}
      </StyledHeadline>
    </StyledIfPopup>
  )
}

export default ModelAndDetailing
